// Auswahllisten für Produktpartner und ihre Ansprechpartner.
//
// Die Ids sind die GESPEICHERTEN Werte (ContactPartnerProfile.position,
// .funktionsbereich, .sparten) und dürfen sich nicht ändern — die Labels
// dagegen schon. Deshalb hier bewusst technische Ids statt der Beschriftungen.

/** Kontaktstatus, an dem ein Produktpartner erkannt wird. */
export const STATUS_PRODUKTPARTNER = "Produktpartner";

/** Kontaktstatus eines Ansprechpartners beim Produktpartner. */
export const STATUS_AP_PRODUKTPARTNER = "Ansprechpartner Produktpartner";

export interface KatalogOption {
  id: string;
  label: string;
}

/**
 * Position im Unternehmen der Gesellschaft. Zwei Einträge tragen zusätzlich
 * einen Freitext: „Assistenz / Sekretariat" das „von: …", „Sonstiges" die
 * „Angabe: …".
 */
export const PP_POSITIONEN: KatalogOption[] = [
  { id: "vorstand", label: "Vorstand / Geschäftsführung" },
  { id: "direktion", label: "Direktion / Bereichsleitung" },
  { id: "abteilungsleitung", label: "Abteilungsleitung" },
  { id: "teamleitung", label: "Teamleitung" },
  { id: "sachbearbeitung", label: "Sachbearbeitung" },
  { id: "key_account", label: "Key Account Management" },
  { id: "assistenz", label: "Assistenz / Sekretariat" },
  { id: "sonstiges", label: "Sonstiges" },
];

/** Position, die zusätzlich „von: …" abfragt. */
export const PP_POSITION_MIT_VON = "assistenz";
/** Position, die zusätzlich „Angabe: …" abfragt. */
export const PP_POSITION_MIT_ANGABE = "sonstiges";

/**
 * Funktionsbereich. Die ersten fünf sind nummeriert — nur bei ihnen ist der
 * Spartenbezug fachlich sinnvoll und wird im Formular eingeblendet.
 */
export const PP_FUNKTIONSBEREICHE: KatalogOption[] = [
  { id: "fb1_allgemein", label: "1 Allgemeiner Ansprechpartner" },
  { id: "fb2_betrieb", label: "2 Betrieb / Vertragsbearbeitung" },
  { id: "fb3_underwriting", label: "3 Underwriting / Risikoprüfung" },
  { id: "fb4_schaden", label: "4 Schaden / Regulierung" },
  { id: "fb5_produkt", label: "5 Produkt / Zeichnungsrichtlinie" },
  { id: "courtage", label: "Courtage / Abrechnung" },
  { id: "vertrieb", label: "Vertrieb / Maklerbetreuung" },
  { id: "recht", label: "Recht / Regress" },
  { id: "compliance", label: "Compliance / Datenschutz" },
  { id: "technik", label: "Technik / Portal" },
  { id: "spezialist", label: "Spezialist / Fachreferent" },
  { id: "sonstiges", label: "Sonstiges" },
];

/** Funktionsbereiche, bei denen der Spartenbezug erscheint (1–5). */
export const PP_FUNKTIONSBEREICHE_MIT_SPARTEN = new Set([
  "fb1_allgemein",
  "fb2_betrieb",
  "fb3_underwriting",
  "fb4_schaden",
  "fb5_produkt",
]);

/** Funktionsbereich, der zusätzlich einen Freitext abfragt. */
export const PP_FUNKTIONSBEREICH_MIT_ANGABE = "sonstiges";

export interface SpartenGruppe {
  /** Überschrift der Gruppe; null = die Gruppe IST die Sparte (ohne Unterpunkte). */
  label: string;
  optionen: KatalogOption[];
}

/**
 * Spartenbezug / Produktbereich, Mehrfachauswahl. Die PK/FK-Unterteilung ist
 * bewusst Teil der Id — eine Person betreut typischerweise nur eine der beiden
 * Seiten.
 *
 * Bewusst NICHT die KMVS-Vertragsartentaxonomie (`insuranceTaxonomy.data.ts`):
 * die kategorisiert nach Zielgruppe („… fürs Unternehmen"), hier geht es um die
 * klassische Spartensystematik der Gesellschaften.
 */
export const PP_SPARTEN: SpartenGruppe[] = [
  {
    label: "Allgemein",
    optionen: [{ id: "uebergreifend", label: "Spartenübergreifend / allgemein" }],
  },
  {
    label: "Kfz",
    optionen: [
      { id: "kfz_pk", label: "Kfz — PK" },
      { id: "kfz_fk", label: "Kfz — FK" },
    ],
  },
  {
    label: "Sach Komposit",
    optionen: [
      { id: "sach_pk", label: "Sach Komposit — PK" },
      { id: "sach_fk", label: "Sach Komposit — FK" },
    ],
  },
  {
    label: "Haftpflicht",
    optionen: [
      { id: "haftpflicht_pk", label: "Haftpflicht — PK" },
      { id: "haftpflicht_fk", label: "Haftpflicht — FK" },
    ],
  },
  {
    label: "Financial Lines",
    optionen: [{ id: "financial_lines", label: "Financial Lines" }],
  },
  {
    label: "Unfall",
    optionen: [
      { id: "unfall_pk", label: "Unfall — PK" },
      { id: "unfall_fk", label: "Unfall — FK" },
    ],
  },
  {
    label: "Transport",
    optionen: [{ id: "transport", label: "Transport" }],
  },
  {
    label: "Technische Versicherungen",
    optionen: [{ id: "technische", label: "Technische Versicherungen" }],
  },
  {
    label: "Rechtsschutz",
    optionen: [
      { id: "rechtsschutz_pk", label: "Rechtsschutz — PK" },
      { id: "rechtsschutz_fk", label: "Rechtsschutz — FK" },
    ],
  },
  {
    label: "Cyber",
    optionen: [
      { id: "cyber_pk", label: "Cyber — PK" },
      { id: "cyber_fk", label: "Cyber — FK" },
    ],
  },
  {
    label: "Kredit / Kaution / Bürgschaft",
    optionen: [{ id: "kredit_kaution", label: "Kredit-Kaution-Bürgschaft" }],
  },
  {
    label: "Leben",
    optionen: [
      { id: "leben_pk", label: "Leben — PK" },
      { id: "leben_fk", label: "Leben — FK" },
    ],
  },
  {
    label: "Kranken",
    optionen: [
      { id: "kranken_normal", label: "Kranken — Normalgeschäft" },
      { id: "kranken_bkv", label: "Kranken — bKV" },
    ],
  },
  {
    label: "Weitere",
    optionen: [
      { id: "spezialkonzepte", label: "Spezialkonzepte" },
      { id: "sonstiges", label: "Sonstiges" },
    ],
  },
];

/** Sparte, die zusätzlich eine Beschreibung abfragt. */
export const PP_SPARTE_SPEZIALKONZEPTE = "spezialkonzepte";
/** Sparte, die zusätzlich eine Beschreibung abfragt. */
export const PP_SPARTE_SONSTIGES = "sonstiges";

/** Alle Sparten flach — für Label-Auflösung in der Anzeige. */
export const PP_SPARTEN_FLACH: KatalogOption[] = PP_SPARTEN.flatMap((g) => g.optionen);

const byId = <T extends KatalogOption>(list: T[], id: string | null | undefined) =>
  list.find((o) => o.id === id);

/** Beschriftung einer Position; unbekannte Ids kommen unverändert zurück. */
export const positionLabel = (id: string | null | undefined): string =>
  byId(PP_POSITIONEN, id)?.label ?? (id ?? "");

/** Beschriftung eines Funktionsbereichs. */
export const funktionsbereichLabel = (id: string | null | undefined): string =>
  byId(PP_FUNKTIONSBEREICHE, id)?.label ?? (id ?? "");

/** Beschriftung einer Sparte. */
export const spartenLabel = (id: string | null | undefined): string =>
  byId(PP_SPARTEN_FLACH, id)?.label ?? (id ?? "");

/** Beschriftungen mehrerer Sparten, in Katalog-Reihenfolge. */
export const spartenLabels = (ids: string[] | null | undefined): string[] => {
  const set = new Set(ids ?? []);
  return PP_SPARTEN_FLACH.filter((o) => set.has(o.id)).map((o) => o.label);
};

export const STATUS_KOOPERATIONSPARTNER = "Kooperationspartner";
export const STATUS_AP_KOOPERATIONSPARTNER = "Ansprechpartner Kooperationspartner";
